import { useState } from 'react';
import {
  Group, Button, TextInput, Textarea, Select, Switch, Stack, NumberInput,
  FileButton, Text, ColorInput, Divider, Box, Center, MultiSelect, Tabs, SegmentedControl,
  SimpleGrid, Paper, Flex, Badge,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconExternalLink, IconDeviceFloppy, IconSun, IconMoon } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import api from '../services/api';
import { BannerPreview } from './BannerPreview';
import {
  BannerTemplate, BannerContent, BannerDesign, BannerLang, BannerTheme, TEMPLATES, TEMPLATE_META,
  LANGS, Lang, templateDefaults, mergeDesign, templateLabel, templateDesc,
} from '../lib/bannerDesign';
import { PREMIUM_UI_ENABLED } from '../lib/features';

export interface Banner {
  id: string; title: string; template: BannerTemplate; content?: BannerContent | null; design?: BannerDesign | null;
  linkScreen?: string | null; linkUrl?: string | null; audience: string; targetUserIds?: string[];
  isActive: boolean; showOnKiosk: boolean; order: number; startsAt?: string | null; endsAt?: string | null;
  createdBy?: { id: string; fullName: string } | null;
}
interface AppUser { id: string; fullName: string; role: string; gender?: 'MALE' | 'FEMALE' | null; phone?: string | null }

/** BannerBuilder → serverga yuboriladigan payload (POST /banners yoki PATCH /banners/:id). */
export interface BannerPayload {
  template: BannerTemplate; content: BannerContent; design: BannerDesign; title: string;
  audience: string; targetUserIds: string[]; linkScreen: string | null; linkUrl: string | null;
  isActive: boolean; showOnKiosk: boolean; order: number; startsAt: string | null; endsAt: string | null;
}

export const AUDIENCES: { value: string; color: string }[] = [
  { value: 'ALL', color: 'gray' },
  { value: 'PARENTS', color: 'grape' },
  { value: 'PARENTS_MALE', color: 'teal' },
  { value: 'PARENTS_FEMALE', color: 'pink' },
  { value: 'TEACHERS', color: 'blue' },
  { value: 'TEACHERS_MALE', color: 'cyan' },
  { value: 'TEACHERS_FEMALE', color: 'violet' },
  { value: 'MALES', color: 'indigo' },
  { value: 'FEMALES', color: 'red' },
  { value: 'CUSTOM', color: 'orange' },
];
export const audMeta = (v: string) => AUDIENCES.find((a) => a.value === v) ?? AUDIENCES[0];

/** Auditoriya kodini joriy tildagi yorliqqa aylantiradi (ro'yxat va detal uchun umumiy). */
export function audienceLabel(t: TFunction, v: string): string {
  const map: Record<string, string> = {
    ALL: t('bannersPage.audAll'),
    PARENTS: t('bannersPage.audParents'),
    PARENTS_MALE: t('bannersPage.audFathers'),
    PARENTS_FEMALE: t('bannersPage.audMothers'),
    TEACHERS: t('bannersPage.audTeachers'),
    TEACHERS_MALE: t('bannersPage.audTeachersMale'),
    TEACHERS_FEMALE: t('bannersPage.audTeachersFemale'),
    MALES: t('bannersPage.audMales'),
    FEMALES: t('bannersPage.audFemales'),
    CUSTOM: t('bannersPage.audCustom'),
  };
  return map[v] ?? map.ALL;
}

/**
 * Banner TV (kiosk) ekranida CHINDAN ko'rinadimi.
 * Backend (`routes/kiosk.ts`) faqat `showOnKiosk = true` VA `audience = ALL`
 * bannerlarni TV'ga chiqaradi — maqsadli bannerlar yo'lakdagi begonalarga
 * ko'rinmasligi kerak. Shu sabab bayroqning o'zi yetarli emas.
 */
export const kioskEffective = (b: Pick<Banner, 'showOnKiosk' | 'audience'>) => !!b.showOnKiosk && b.audience === 'ALL';

/**
 * "Kiosk" belgisi — HAQIQIY holatni aks ettiradi: bayroq yoqilgan, ammo
 * auditoriya toraytirilgan bo'lsa (eski yozuvlar) belgi ogohlantiruvchi rangda
 * "chiqmaydi" izohi bilan chiqadi, aks holda panel yolg'on ma'lumot bergan bo'lardi.
 * Ro'yxat va detal sahifalari uchun umumiy.
 */
export function KioskBadge({ banner }: { banner: Pick<Banner, 'showOnKiosk' | 'audience'> }) {
  const { t } = useTranslation();
  if (!banner.showOnKiosk) return null;
  const live = kioskEffective(banner);
  return (
    <Badge variant="light" color={live ? 'violet' : 'orange'} title={live ? undefined : t('bannersPage.kioskAllOnly')}>
      {live ? t('bannersPage.kioskBadge') : t('bannersPage.kioskBadgeInactive')}
    </Badge>
  );
}

/**
 * Preview uchun light/dark almashtirgichi — konstruktor va banner detali uchun UMUMIY
 * (ikki joyda bir xil ko'rinishi shart). Emoji o'rniga ikonka: loyiha qoidasi bo'yicha
 * UI'da emoji-stiker ishlatilmaydi.
 */
export const themeSwitchData = (t: TFunction) => [
  { value: 'light', label: <Center style={{ gap: 6 }}><IconSun size={14} />{t('bannersPage.themeLight')}</Center> },
  { value: 'dark', label: <Center style={{ gap: 6 }}><IconMoon size={14} />{t('bannersPage.themeDark')}</Center> },
];

const SCREENS = ['', 'Announcements', 'Attendance', 'Grades', 'Homework', 'Payments', 'Profile', 'Premium'];

/**
 * Bannerga tanlash mumkin bo'lgan ekranlar.
 * Premium muzlatilganda (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`) "Premium"
 * taklif QILINMAYDI: ilovada bu ekran navigatordan olib tashlangan va unga havola
 * qiluvchi bannerlar umuman chizilmaydi (`app/src/components/HomeBanners.tsx`) —
 * ya'ni admin ko'rinmaydigan banner yasab qo'ymasin. `screenLabel()` da yorliq
 * saqlangan: eski bannerlar ro'yxatda baribir to'g'ri nom bilan ko'rinadi.
 */
const LINK_SCREENS = PREMIUM_UI_ENABLED ? SCREENS : SCREENS.filter((s) => s !== 'Premium');

/** Ekran ID → tarjima qilingan yorliq (ilova ekranlari; xom ID ko'rsatmaslik uchun). */
function screenLabel(t: (k: string) => string, s: string): string {
  if (!s) return '—';
  const map: Record<string, string> = {
    Announcements: t('nav.announcements'), Attendance: t('nav.attendance'), Grades: t('nav.grades'),
    Homework: t('nav.homework'), Payments: t('nav.payments'), Profile: t('bannersPage.screenProfile'), Premium: t('nav.premium'),
  };
  return map[s] ?? s;
}
const LANG_LABEL: Record<Lang, string> = { uz: "O'zbek", ru: 'Русский', en: 'English' };

interface Draft {
  template: BannerTemplate;
  content: BannerContent;
  design: BannerDesign;
  audience: string;
  targetUserIds: string[];
  linkScreen: string;
  linkUrl: string;
  isActive: boolean;
  showOnKiosk: boolean;
  order: number;
  startsAt: Date | null;
  endsAt: Date | null;
}

const draftFromBanner = (b?: Banner | null): Draft => b ? {
  template: b.template,
  content: b.content ?? { uz: {}, ru: {}, en: {} },
  design: mergeDesign(b.template, b.design),
  audience: b.audience,
  targetUserIds: b.targetUserIds ?? [],
  linkScreen: b.linkScreen ?? '',
  linkUrl: b.linkUrl ?? '',
  isActive: b.isActive,
  showOnKiosk: !!b.showOnKiosk,
  order: b.order,
  startsAt: b.startsAt ? new Date(b.startsAt) : null,
  endsAt: b.endsAt ? new Date(b.endsAt) : null,
} : {
  template: 'SOLID',
  content: { uz: {}, ru: {}, en: {} },
  design: mergeDesign('SOLID', templateDefaults('SOLID')),
  audience: 'ALL', targetUserIds: [], linkScreen: '', linkUrl: '', isActive: true, showOnKiosk: false, order: 0, startsAt: null, endsAt: null,
};

interface BannerBuilderProps {
  /** Tahrirlash uchun mavjud banner; yaratish uchun bo'sh qoldiring. */
  initial?: Banner | null;
  /** "Saqlash" bosilganda tayyor payload bilan chaqiriladi (POST yoki PATCH ni chaqiruvchi hal qiladi). */
  onSubmit: (payload: BannerPayload) => void;
  submitting?: boolean;
  /** Berilsa, chapda "Bekor qilish" tugmasi chiqadi (modal uchun). */
  onCancel?: () => void;
  submitLabel?: string;
}

/**
 * Banner dizayn konstruktori — shablon + 3 til kontent + light/dark dizayn + auditoriya + jonli preview.
 * Yaratish (Banners modal) va tahrirlash (BannerDetail) uchun umumiy.
 */
export function BannerBuilder({ initial, onSubmit, submitting, onCancel, submitLabel }: BannerBuilderProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => draftFromBanner(initial));
  const [prevTheme, setPrevTheme] = useState<'light' | 'dark'>('light');
  const [prevLang, setPrevLang] = useState<Lang>('uz');
  const [uploading, setUploading] = useState<'light' | 'dark' | null>(null);

  const { data: appUsers = [] } = useQuery({
    queryKey: ['banner-users'],
    queryFn: async () => (await api.get<AppUser[]>('/banners/users')).data,
    enabled: draft.audience === 'CUSTOM',
  });

  const audLabel = (v: string) => audienceLabel(t, v);
  const FIELD_LABEL: Record<keyof BannerLang, string> = {
    title: t('bannersPage.fieldTitle'), body: t('bannersPage.fieldBody'),
    cta: t('bannersPage.fieldCta'), eyebrow: t('bannersPage.fieldEyebrow'),
  };

  const roleTag = (u: AppUser) => u.role === 'PARENT'
    ? (u.gender === 'MALE' ? t('bannersPage.roleFather') : u.gender === 'FEMALE' ? t('bannersPage.roleMother') : t('roles.PARENT'))
    : (u.gender === 'MALE' ? t('bannersPage.roleTeacherMale') : t('roles.TEACHER'));
  const userOptions = appUsers.map((u) => ({ value: u.id, label: `${u.fullName} — ${roleTag(u)}${u.phone ? ` (${u.phone})` : ''}` }));

  // ── helpers ──
  const setTemplate = (tpl: BannerTemplate) => setDraft((d) => ({ ...d, template: tpl, design: mergeDesign(tpl, d.design) }));
  const setContent = (lang: Lang, field: keyof BannerLang, val: string) =>
    setDraft((d) => ({ ...d, content: { ...d.content, [lang]: { ...(d.content[lang] || {}), [field]: val } } }));
  const setDesign = (patch: Partial<BannerDesign>) => setDraft((d) => ({ ...d, design: { ...d.design, ...patch } }));
  const setTheme = (theme: 'light' | 'dark', patch: Partial<BannerTheme>) =>
    setDraft((d) => ({ ...d, design: { ...d.design, [theme]: { ...(d.design[theme] || {}), ...patch } } }));
  /*
   * Quyidagi uchtasi ALOHIDA yordamchi bo'lishining sababi: qiymat updater'ga
   * TAYYOR holda kiradi. React dispatch tugagach hodisaning `currentTarget` ini
   * `null` qiladi — uni `setDraft((d) => ...)` ICHIDA o'qish (StrictMode'da updater
   * ikki marta chaqiriladi) sahifani oq ekranga aylantirardi. Namuna: `pages/Kiosk.tsx`
   * dagi `onLoad` — `currentTarget` doim updater TASHQARISIDA o'qiladi.
   */
  const setLink = (linkUrl: string) => setDraft((d) => ({ ...d, linkUrl }));
  const setActive = (isActive: boolean) => setDraft((d) => ({ ...d, isActive }));
  const setKiosk = (showOnKiosk: boolean) => setDraft((d) => ({ ...d, showOnKiosk }));

  const uploadImage = async (theme: 'light' | 'dark', file: File | null) => {
    if (!file) return;
    setUploading(theme);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post<{ imageKey: string }>('/banners/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTheme(theme, { imageKey: data.imageKey });
    } catch (e) {
      notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('bannersPage.imageUploadFailed') });
    } finally { setUploading(null); }
  };

  const submit = () => {
    const d = draft;
    const payload: BannerPayload = {
      template: d.template, content: d.content, design: d.design,
      title: d.content.uz?.title || TEMPLATE_META[d.template].label,
      audience: d.audience, targetUserIds: d.targetUserIds,
      linkScreen: d.linkScreen || null, linkUrl: d.linkUrl || null,
      // Kiosk bayrog'i SERVER qoidasi bilan bir xil normallashtiriladi: `routes/banners.ts`
      // `showOnKiosk` ni `audience !== ALL` bo'lganda majburan `false` ga tushiradi
      // (TV — ochiq ekran, maqsadli banner u yerda chiqmasligi kerak). Front ham aynan
      // shuni yuboradi, aks holda panel "belgi saqlandi" deb ko'rsatib, bazada esa
      // o'chgan bo'lardi. Drafdagi qiymat o'zgarmaydi — foydalanuvchi auditoriyani
      // saqlashdan OLDIN "Hammaga" ga qaytarsa, tanlovi o'z holicha ketadi.
      isActive: d.isActive, showOnKiosk: d.showOnKiosk && d.audience === 'ALL', order: d.order,
      startsAt: d.startsAt ? d.startsAt.toISOString() : null,
      endsAt: d.endsAt ? d.endsAt.toISOString() : null,
    };
    onSubmit(payload);
  };

  const meta = TEMPLATE_META[draft.template];
  const merged = mergeDesign(draft.template, draft.design);

  /**
   * ESKI banner muzlatilgan ekranga (`Premium`) havola qilishi mumkin.
   * Ikki narsa kerak:
   *  1) qiymat ro'yxatda TURSIN — aks holda tahrirlashda maydon BO'SH ko'rinardi va
   *     admin bannerda havola umuman yo'q deb o'ylardi (aslida saqlanib turibdi);
   *  2) ochiq OGOHLANTIRISH — ilova bunday bannerni umuman chizmaydi
   *     (`app/src/components/HomeBanners.tsx`), ya'ni banner "faol" ko'rinib, hech kimga
   *     ko'rinmay turadi. Avval admin panelda bu haqda hech qanday belgi yo'q edi.
   */
  const frozenScreen = !!draft.linkScreen && !LINK_SCREENS.includes(draft.linkScreen);
  const screenOptions = (frozenScreen ? [...LINK_SCREENS, draft.linkScreen] : LINK_SCREENS)
    .map((s) => ({ value: s, label: screenLabel(t, s) }));

  return (
    <Flex align="flex-start" gap="xl" wrap="wrap">
      {/* Chap: kontrollar */}
      <Stack style={{ flex: 1, minWidth: 320 }} maw={640}>
        {/* Shablon */}
        <Box>
          <Text fw={600} mb={6}>{t('bannersPage.template')}</Text>
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
            {TEMPLATES.map((tpl) => (
              <Paper key={tpl} withBorder p="xs" onClick={() => setTemplate(tpl)}
                style={{ cursor: 'pointer', borderColor: draft.template === tpl ? 'var(--mantine-color-brand-5)' : undefined, borderWidth: draft.template === tpl ? 2 : 1 }}>
                <Text fw={600} size="sm">{templateLabel(t, tpl)}</Text>
                <Text size="xs" c="dimmed" lineClamp={2}>{templateDesc(t, tpl)}</Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        {/* Kontent — 3 til */}
        <Box>
          <Text fw={600} mb={6}>{t('bannersPage.content')}</Text>
          <Tabs defaultValue="uz" variant="outline">
            <Tabs.List>{LANGS.map((l) => <Tabs.Tab key={l} value={l}>{LANG_LABEL[l]}</Tabs.Tab>)}</Tabs.List>
            {LANGS.map((l) => (
              <Tabs.Panel key={l} value={l} pt="sm">
                <Stack gap="xs">
                  {meta.contentFields.map((f) => (
                    f === 'body' ? (
                      <Textarea key={f} label={FIELD_LABEL[f]} autosize minRows={2} value={draft.content[l]?.[f] ?? ''} onChange={(e) => setContent(l, f, e.currentTarget.value)} />
                    ) : (
                      <TextInput key={f} label={FIELD_LABEL[f]} value={draft.content[l]?.[f] ?? ''} onChange={(e) => setContent(l, f, e.currentTarget.value)} />
                    )
                  ))}
                  {l !== 'uz' && <Text size="xs" c="dimmed">{t('bannersPage.fallbackHint')}</Text>}
                </Stack>
              </Tabs.Panel>
            ))}
          </Tabs>
        </Box>

        {/* Dizayn — light/dark */}
        <Box>
          <Group justify="space-between" mb={6}>
            <Text fw={600}>{t('bannersPage.design')}</Text>
            <Group gap="xs">
              {meta.ctrl.align && (
                <SegmentedControl size="xs" value={merged.align} onChange={(v) => setDesign({ align: v as 'left' | 'center' })}
                  data={[{ value: 'left', label: t('bannersPage.alignLeft') }, { value: 'center', label: t('bannersPage.alignCenter') }]} />
              )}
              {(draft.template === 'HERO' || draft.template === 'GRADIENT') && (
                <Switch size="xs" label="CTA" checked={merged.showCta} onChange={(e) => setDesign({ showCta: e.currentTarget.checked })} />
              )}
              {draft.template === 'HERO' && (
                <Switch size="xs" label={t('bannersPage.scrim')} checked={merged.scrim} onChange={(e) => setDesign({ scrim: e.currentTarget.checked })} />
              )}
            </Group>
          </Group>
          {meta.ctrl.emoji && (
            <TextInput label={t('bannersPage.emoji')} w={140} mb="sm" value={merged.emoji} onChange={(e) => setDesign({ emoji: e.currentTarget.value })} placeholder="📌" />
          )}
          <Tabs defaultValue="light" variant="pills">
            <Tabs.List mb="sm">
              <Tabs.Tab value="light" leftSection={<IconSun size={15} />}>{t('bannersPage.themeLight')}</Tabs.Tab>
              <Tabs.Tab value="dark" leftSection={<IconMoon size={15} />}>{t('bannersPage.themeDark')}</Tabs.Tab>
            </Tabs.List>
            {(['light', 'dark'] as const).map((theme) => {
              const th = merged[theme];
              return (
                <Tabs.Panel key={theme} value={theme}>
                  <Stack gap="xs">
                    {meta.ctrl.gradient && (draft.template === 'SOLID') && (
                      <Switch label={t('bannersPage.gradientBg')} checked={!!th.useGradient} onChange={(e) => setTheme(theme, { useGradient: e.currentTarget.checked })} />
                    )}
                    {meta.ctrl.solidBg && !th.useGradient && (
                      <ColorInput label={t('bannersPage.bgColor')} format="hex" value={th.bg ?? ''} onChange={(v) => setTheme(theme, { bg: v })} />
                    )}
                    {meta.ctrl.gradient && (draft.template !== 'SOLID' || th.useGradient) && (
                      <Group grow>
                        <ColorInput label={t('bannersPage.gradient1')} format="hex" value={th.grad1 ?? ''} onChange={(v) => setTheme(theme, { grad1: v })} />
                        <ColorInput label={t('bannersPage.gradient2')} format="hex" value={th.grad2 ?? ''} onChange={(v) => setTheme(theme, { grad2: v })} />
                      </Group>
                    )}
                    {meta.ctrl.ticketBg && (
                      <ColorInput label={t('bannersPage.ticketBg')} format="hex" value={th.bg ?? ''} onChange={(v) => setTheme(theme, { bg: v })} />
                    )}
                    {meta.ctrl.accent && (
                      <ColorInput label={t('bannersPage.accentColor')} format="hex" value={th.accent ?? ''} onChange={(v) => setTheme(theme, { accent: v })} />
                    )}
                    {meta.ctrl.textTone && (
                      <SegmentedControl value={th.text ?? 'light'} onChange={(v) => setTheme(theme, { text: v as 'light' | 'dark' })}
                        data={[{ value: 'light', label: t('bannersPage.textLight') }, { value: 'dark', label: t('bannersPage.textDark') }]} />
                    )}
                    {meta.ctrl.image && (
                      <Group align="flex-end">
                        <FileButton onChange={(f) => uploadImage(theme, f)} accept="image/png,image/webp,image/jpeg,image/gif">
                          {(props) => <Button {...props} size="xs" variant="light" loading={uploading === theme}>{t('bannersPage.uploadImage')}</Button>}
                        </FileButton>
                        {th.imageKey && <Button size="xs" variant="subtle" color="red" onClick={() => setTheme(theme, { imageKey: undefined })}>{t('bannersPage.removeImage')}</Button>}
                      </Group>
                    )}
                  </Stack>
                </Tabs.Panel>
              );
            })}
          </Tabs>
        </Box>

        <Divider />

        {/* Auditoriya */}
        <Select label={t('bannersPage.audience')} allowDeselect={false} data={AUDIENCES.map((a) => ({ value: a.value, label: audLabel(a.value) }))}
          value={draft.audience} onChange={(v) => setDraft((d) => ({ ...d, audience: v ?? 'ALL' }))} />
        {draft.audience === 'CUSTOM' && (
          <MultiSelect label={t('bannersPage.users')} description={t('bannersPage.usersDesc')} searchable clearable hidePickedOptions
            placeholder={t('bannersPage.usersPlaceholder')} data={userOptions} maxDropdownHeight={240}
            value={draft.targetUserIds} onChange={(v) => setDraft((d) => ({ ...d, targetUserIds: v }))} />
        )}

        {/* Havola */}
        <Stack gap={4}>
          <Group grow>
            <Select label={t('bannersPage.linkScreen')} data={screenOptions}
              value={draft.linkScreen} onChange={(v) => setDraft((d) => ({ ...d, linkScreen: v ?? '' }))} />
            <TextInput label={t('bannersPage.linkUrl')} placeholder="https://..." leftSection={<IconExternalLink size={14} />}
              value={draft.linkUrl} onChange={(e) => setLink(e.currentTarget.value)} />
          </Group>
          {frozenScreen && (
            <Text size="xs" c="orange">{t('bannersPage.linkScreenFrozen')}</Text>
          )}
        </Stack>

        {/* Ko'rsatish */}
        <Group grow align="flex-end">
          <DateInput label={t('bannersPage.startsAt')} placeholder={t('bannersPage.optional')} valueFormat="DD.MM.YYYY" clearable value={draft.startsAt} onChange={(v) => setDraft((d) => ({ ...d, startsAt: v }))} />
          <DateInput label={t('bannersPage.endsAt')} placeholder={t('bannersPage.optional')} valueFormat="DD.MM.YYYY" clearable value={draft.endsAt} onChange={(v) => setDraft((d) => ({ ...d, endsAt: v }))} />
          <NumberInput label={t('bannersPage.order')} w={90} value={draft.order} onChange={(v) => setDraft((d) => ({ ...d, order: Number(v) || 0 }))} />
        </Group>
        <Switch label={t('bannersPage.activeLabel')} checked={draft.isActive} onChange={(e) => setActive(e.currentTarget.checked)} />
        {/*
          Kiosk (TV) ekraniga backend faqat `audience = ALL` bannerlarni chiqaradi
          (backend `routes/kiosk.ts`): maqsadli bannerlar yo'lakdagi begonalarga
          ko'rinmasligi kerak. Auditoriya toraytirilganda switch O'CHIRILADI (disabled) va
          SAQLASHDA bayroq `false` bo'lib ketadi — bu server qoidasi (`routes/banners.ts`),
          front uni yashirmaydi, yonidagi matnda ochiq yozib qo'yadi. Draft qiymati esa
          o'zgarmaydi: foydalanuvchi saqlashdan oldin auditoriyani "Hammaga" ga qaytarsa,
          tanlovi o'z holicha ketadi.
        */}
        <Stack gap={4}>
          <Switch label={t('bannersPage.kioskLabel')} description={t('bannersPage.kioskDesc')} color="violet"
            disabled={draft.audience !== 'ALL'}
            checked={draft.showOnKiosk}
            onChange={(e) => setKiosk(e.currentTarget.checked)} />
          {draft.audience !== 'ALL' && (
            <Text size="xs" c={draft.showOnKiosk ? 'orange' : 'dimmed'}>
              {draft.showOnKiosk ? t('bannersPage.kioskClearedWarn') : t('bannersPage.kioskAllOnly')}
            </Text>
          )}
        </Stack>

        <Group justify="flex-end" mt="md">
          {onCancel && <Button variant="default" onClick={onCancel}>{t('common.cancel')}</Button>}
          <Button leftSection={<IconDeviceFloppy size={16} />} loading={submitting} onClick={submit}>{submitLabel ?? t('common.save')}</Button>
        </Group>
      </Stack>

      {/* O'ng: jonli preview */}
      <Box style={{ width: 340, flexShrink: 0, position: 'sticky', top: 0 }}>
        <Text fw={600} mb="sm">{t('bannersPage.livePreview')}</Text>
        <Group mb="md" gap="xs">
          <SegmentedControl size="xs" value={prevTheme} onChange={(v) => setPrevTheme(v as 'light' | 'dark')}
            data={themeSwitchData(t)} />
          <SegmentedControl size="xs" value={prevLang} onChange={(v) => setPrevLang(v as Lang)}
            data={LANGS.map((l) => ({ value: l, label: l.toUpperCase() }))} />
        </Group>
        <Center>
          <BannerPreview banner={{ template: draft.template, content: draft.content, design: draft.design, linkScreen: draft.linkScreen, linkUrl: draft.linkUrl }}
            theme={prevTheme} lang={prevLang} width={300} />
        </Center>
      </Box>
    </Flex>
  );
}
